"use strict";
/**
 * Provides several arithmetic operations (mainly in a circle group) which are
 * called only internally.
 *
 * Note that no functions take `multiple laps' into consideration: i.e. the arc
 * length of the interval [-2pi, 2pi] is not 4pi but 0.
 * @module
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.multMatrixMatrix = exports.multMatrixVector = exports.circularDelta = exports.circularLerp = exports.circularClamp = exports.circularNearer = exports.polarToCartesian = exports.cartesianToPolar = exports.clamp = exports.mod = exports.TWO_PI = void 0;
exports.TWO_PI = Math.PI + Math.PI;
var mod = function (dividend, divisor) {
    var x = dividend % divisor;
    if (x >= 0) {
        return x;
    }
    else {
        return x + divisor;
    }
};
exports.mod = mod;
var clamp = function (x, min, max) {
    if (x < min)
        return min;
    else if (x > max)
        return max;
    return x;
};
exports.clamp = clamp;
var cartesianToPolar = function (x, y, perimeter) {
    if (perimeter === void 0) { perimeter = exports.TWO_PI; }
    var factor = perimeter / exports.TWO_PI;
    return [Math.sqrt(x * x + y * y), exports.mod(Math.atan2(y, x) * factor, 360)];
};
exports.cartesianToPolar = cartesianToPolar;
var polarToCartesian = function (r, theta, perimeter) {
    if (perimeter === void 0) { perimeter = exports.TWO_PI; }
    var factor = exports.TWO_PI / perimeter;
    var hueRad = theta * factor;
    return [r * Math.cos(hueRad), r * Math.sin(hueRad)];
};
exports.polarToCartesian = polarToCartesian;
/**
 * Compares the counterclockwise distances between theta1 and x and
 * between x and theta2, and returns theta1 or theta2 whichever is
 * nearer.
 * @param {number} x - must be in the counterclockwise interval [min, max]
 * @param {number} theta1
 * @param {number} theta2
 * @param {number} [perimeter = TWO_PI]
 */
var circularNearer = function (x, theta1, theta2, perimeter) {
    if (perimeter === void 0) { perimeter = exports.TWO_PI; }
    if (exports.mod(x - theta1, perimeter) <= exports.mod(theta2 - x, perimeter)) {
        return theta1;
    }
    else {
        return theta2;
    }
};
exports.circularNearer = circularNearer;
/**
 * Is a clamp function in a circle group. If x is not in the
 * (counterclockwise) closed interval [min, max], circularClamp returns
 * min or max whichever is nearer to x.
 * @param {number} x
 * @param {number} min
 * @param {number} max
 * @param {number} [perimeter = TWO_PI]
 */
var circularClamp = function (x, min, max, perimeter) {
    if (perimeter === void 0) { perimeter = exports.TWO_PI; }
    var xMod = exports.mod(x, perimeter);
    var minMod = exports.mod(min, perimeter);
    var maxMod = exports.mod(max, perimeter);
    if (isNaN(xMod) || isNaN(minMod) || isNaN(maxMod)) {
        return NaN;
    }
    if (minMod <= maxMod) {
        if (minMod <= xMod && xMod <= maxMod) {
            return x;
        }
        else {
            // minMod <= maxMod < xMod or xMod < minMod <= maxMod.
            return exports.circularNearer(x, max, min);
        }
    }
    else {
        if (xMod <= maxMod || minMod <= xMod) {
            return x; // xMod <= maxMod < minMod or maxMod < minMod <= xMod
        }
        else {
            return exports.circularNearer(x, max, min); // maxMod < xMod < minMod
        }
    }
};
exports.circularClamp = circularClamp;
/**
 * Is a counterclockwise linear interpolation from theta1 to theta2 in a
 * circle group. It is guaranteed that the returned value is within the
 * given interval from theta1 to theta2 if coef is in [0, 1].
 * @param {number} coef - should be in [0, 1]
 * @param {number} theta1
 * @param {number} theta2
 * @param {number} [perimeter = TWO_PI]
 */
var circularLerp = function (coef, theta1, theta2, perimeter) {
    if (perimeter === void 0) { perimeter = exports.TWO_PI; }
    var arcLength = exports.mod(theta2 - theta1, perimeter);
    return exports.circularClamp(theta1 + arcLength * coef, theta1, theta2, perimeter);
};
exports.circularLerp = circularLerp;
/**
 * Returns the 'difference' of two values in a circle group. The returned value
 * Δ satisfies theta2 + Δ ≡ theta1 and -perimeter/2 <= Δ <= perimeter/2.
 * @param {number} theta1
 * @param {number} theta2
 * @param {number} [perimter = TWO_PI]
 */
var circularDelta = function (theta1, theta2, perimeter) {
    if (perimeter === void 0) { perimeter = exports.TWO_PI; }
    var d = exports.mod(theta1 - theta2, perimeter);
    if (d <= perimeter / 2) {
        return d;
    }
    else {
        return d - perimeter;
    }
};
exports.circularDelta = circularDelta;
// We need only the following two kinds of multiplication.
var multMatrixVector = function (A, x) {
    return [
        A[0][0] * x[0] + A[0][1] * x[1] + A[0][2] * x[2],
        A[1][0] * x[0] + A[1][1] * x[1] + A[1][2] * x[2],
        A[2][0] * x[0] + A[2][1] * x[1] + A[2][2] * x[2],
    ];
};
exports.multMatrixVector = multMatrixVector;
var multMatrixMatrix = function (A, B) {
    return [
        [
            A[0][0] * B[0][0] + A[0][1] * B[1][0] + A[0][2] * B[2][0],
            A[0][0] * B[0][1] + A[0][1] * B[1][1] + A[0][2] * B[2][1],
            A[0][0] * B[0][2] + A[0][1] * B[1][2] + A[0][2] * B[2][2],
        ],
        [
            A[1][0] * B[0][0] + A[1][1] * B[1][0] + A[1][2] * B[2][0],
            A[1][0] * B[0][1] + A[1][1] * B[1][1] + A[1][2] * B[2][1],
            A[1][0] * B[0][2] + A[1][1] * B[1][2] + A[1][2] * B[2][2],
        ],
        [
            A[2][0] * B[0][0] + A[2][1] * B[1][0] + A[2][2] * B[2][0],
            A[2][0] * B[0][1] + A[2][1] * B[1][1] + A[2][2] * B[2][1],
            A[2][0] * B[0][2] + A[2][1] * B[1][2] + A[2][2] * B[2][2],
        ],
    ];
};
exports.multMatrixMatrix = multMatrixMatrix;
//# sourceMappingURL=arithmetic.js.map